import { EVENT_PROPERTIES, EVENT_STATUS, SCHEDULE_EVENT_TYPE } from '@/constants/device';
import {
    getProfilePicUrl,
    getProfilePicUrlFromArray,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
    PhotoResolution,
    VideoCallStatusUpdate,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { get, isEmpty } from 'lodash';
import { useContext, useEffect } from 'react';
import { DashboardReportContext } from './DashboardContext';
import {
    formatTime,
    getCallTitle,
    getDateLabel,
    isCallInProgress,
    RECENT_ACTIVITY_CARD_TYPES,
    sortByLatestTimestamp,
} from './Home/RecentActivity/RecentActivityUtils';

/**
 * Registers a section's report-ready payload with the dashboard report context.
 * The payload is retained even after the component unmounts so that the export
 * control can build a report regardless of the currently active tab.
 */
export const useRegisterReportSection = (key, payload, deps = []) => {
    const { registerReportSection } = useContext(DashboardReportContext);
    useEffect(() => {
        registerReportSection?.(key, payload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};

// Card types whose `data` is a single normalized biometric object
// ({ data: [{ display_label, display_value }], utc_timestamp_inms }).
const BIOMETRIC_CARD_TYPES = [
    RECENT_ACTIVITY_CARD_TYPES.BLOOD_PRESSURE,
    RECENT_ACTIVITY_CARD_TYPES.GLUCOSE_METER,
    RECENT_ACTIVITY_CARD_TYPES.PULSE_OXIMETER,
    RECENT_ACTIVITY_CARD_TYPES.THERMOMETER,
    RECENT_ACTIVITY_CARD_TYPES.WEIGHT_SCALE,
];

// Translation keys for card titles, with a readable fallback derived from the key.
const CARD_TITLE_KEYS = {
    [RECENT_ACTIVITY_CARD_TYPES.RECENT_CALLS]: 'RECENT_CALLS',
    [RECENT_ACTIVITY_CARD_TYPES.MOTION]: 'MOTION',
    [RECENT_ACTIVITY_CARD_TYPES.ENGAGEMENT]: 'ENGAGEMENT',
    [RECENT_ACTIVITY_CARD_TYPES.BLOOD_PRESSURE]: 'BLOODPRESSURE',
    [RECENT_ACTIVITY_CARD_TYPES.GLUCOSE_METER]: 'GLUCOSE',
    [RECENT_ACTIVITY_CARD_TYPES.PULSE_OXIMETER]: 'PULSE_OXIMETER',
    [RECENT_ACTIVITY_CARD_TYPES.THERMOMETER]: 'THERMOMETER',
    [RECENT_ACTIVITY_CARD_TYPES.WEIGHT_SCALE]: 'WEIGHT_SCALE',
    [RECENT_ACTIVITY_CARD_TYPES.DOOR_WINDOW_SENSOR]: 'CONTACT_SENSOR',
    [RECENT_ACTIVITY_CARD_TYPES.ENVIRONMENTAL_SENSOR]: 'ENVIRONMENTAL',
    [RECENT_ACTIVITY_CARD_TYPES.OCCUPANCY_SENSOR]: 'OCCUPANCY_SENSOR',
    [RECENT_ACTIVITY_CARD_TYPES.SMART_PLUG]: 'SMART_PLUG',
    [RECENT_ACTIVITY_CARD_TYPES.TOILET_FLUSH_SENSOR]: 'TOILET_FLUSH_SENSOR',
};

const prettify = (key = '') =>
    String(key)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

const resolveTitle = (trans, key) => {
    const translationKey = CARD_TITLE_KEYS[key];
    if (!translationKey) return prettify(key);
    const translated = trans(translationKey);
    return !translated || translated === translationKey ? prettify(key) : translated;
};

const getEntryTimestamp = (entry) =>
    get(entry, 'utc_timestamp_inms', '') || get(entry, EVENT_PROPERTIES.TIMESTAMP, '');

const formatWhen = (timestamp, ctx) => {
    if (!timestamp) return '-';
    const dateLabel = getDateLabel(timestamp, ctx.trans, ctx.timezone, ctx.locale);
    const timeLabel = formatTime(timestamp, ctx.is24HourFormat, ctx.timezone);
    return [dateLabel, timeLabel].filter(Boolean).join(' ').trim() || '-';
};

const summarizeBiometric = (entry, ctx) => {
    const dataArray = get(entry, 'data', []);
    const value = (Array.isArray(dataArray) ? dataArray : [])
        .map((point) => {
            const label = get(point, 'display_label', '');
            const displayValue = get(point, 'display_value', '');
            return `${label ? `${label} ` : ''}${displayValue}`.trim();
        })
        .filter(Boolean)
        .join(', ');
    return { value: value || '-', when: formatWhen(getEntryTimestamp(entry), ctx) };
};

const summarizeEventList = (list, ctx, valueBuilder) => {
    const events = Array.isArray(list) ? list : [];
    const sorted = sortByLatestTimestamp(events, EVENT_PROPERTIES.TIMESTAMP);
    const latest = sorted[0] || {};
    return {
        value: valueBuilder ? valueBuilder(latest, events, ctx) : '-',
        when: formatWhen(getEntryTimestamp(latest), ctx),
    };
};

// Resolves a call notification to a status badge key (see reportGraphics).
const resolveCallStatusKey = (notification) => {
    const eventStatus = notification?.event_status;
    const callStatus = notification?.call_status__status || notification?.call_status;
    if (eventStatus === EVENT_STATUS.COMPLETED || callStatus === VideoCallStatusUpdate.completed) {
        return 'completed';
    }
    if (eventStatus === EVENT_STATUS.EXPIRED || callStatus === VideoCallStatusUpdate.missed) {
        return 'missed';
    }
    if (callStatus === VideoCallStatusUpdate.declined) return 'declined';
    if (eventStatus === EVENT_STATUS.ONGOING && isCallInProgress(notification)) return 'ongoing';
    if (
        !callStatus ||
        callStatus === VideoCallStatusUpdate.calling ||
        callStatus === VideoCallStatusUpdate.invited ||
        callStatus === VideoCallStatusUpdate.ringing ||
        callStatus === VideoCallStatusUpdate.accepted ||
        callStatus === VideoCallStatusUpdate.joining
    ) {
        return 'scheduled';
    }
    return 'failed';
};

// Translation keys for the call-status pill label.
const STATUS_LABEL_KEYS = {
    completed: 'COMPLETED',
    missed: 'MISSED',
    declined: 'DECLINED',
    failed: 'FAILED',
    scheduled: 'SCHEDULED',
    ongoing: 'INPROGRESS',
};

// Formats a call duration (seconds) into a short human label. Sub-minute calls
// round up to one minute (matching the Call Activity list).
const formatCallDuration = (seconds) => {
    const total = Number(seconds);
    if (!total || total <= 0) return null;
    if (total <= 60) return '1 min';
    const minutes = Math.ceil(total / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0 ? `${hours} hr ${remainingMinutes} min` : `${remainingMinutes} min`;
};

// Builds compact per-call rows (caller, photo, status, direction, duration) for
// the report. Photo URLs are resolved to data URLs later, at export time.
const buildCallRows = (list, ctx) => {
    const events = Array.isArray(list) ? list : [];
    const sorted = sortByLatestTimestamp(events, 'utc_timestamp_inms');
    return sorted.slice(0, 5).map((notification) => {
        const invited = notification?.invited_contacts || [];
        const isAdhoc = notification?.incoming !== undefined && invited.length <= 2;
        const other = ctx.useruuid
            ? invited.find((c) => c?.useruuid && c.useruuid !== ctx.useruuid) || invited[0]
            : invited[0];
        const photoUrl = other
            ? getProfilePicUrl(other.useruuid, ctx.resourcesMap, PhotoResolution.R64) ||
            getProfilePicUrlFromArray(other.resources, PhotoResolution.R64) ||
            null
            : null;
        const timestamp =
            get(notification, 'utc_timestamp_inms', '') || getEntryTimestamp(notification);
        const statusKey = resolveCallStatusKey(notification);
        const statusLabelKey = STATUS_LABEL_KEYS[statusKey];
        return {
            callerName: getCallTitle(notification, ctx.trans, ctx.useruuid),
            photoUrl,
            statusKey,
            statusLabel: (statusLabelKey && ctx.trans(statusLabelKey)) || '',
            durationLabel: statusKey === 'completed' ? formatCallDuration(notification?.duration) : null,
            direction:
                isAdhoc && notification?.event_type !== SCHEDULE_EVENT_TYPE.SCHEDULE
                    ? notification.incoming
                        ? 'incoming'
                        : 'outgoing'
                    : null,
            when: formatWhen(timestamp, ctx),
        };
    });
};

// Builds compact engagement rows (channel/app watched + when), modeled like the
// call list. Engagement `data` is an object keyed by event type.
const buildEngagementRows = (data, ctx) => {
    if (!data || typeof data !== 'object') return [];
    return Object.entries(data)
        .map(([eventType, value]) => {
            const name =
                get(value, EVENT_PROPERTIES.CHANNEL_NAME, '') ||
                get(value, EVENT_PROPERTIES.APP_NAME, '') ||
                prettify(eventType);
            const ts =
                get(value, EVENT_PROPERTIES.UTC_TIMESTAMP, '') ||
                get(value, EVENT_PROPERTIES.TIMESTAMP, '') ||
                get(value, 'utc_timestamp', '');
            return { name, when: formatWhen(ts, ctx), timestampMs: new Date(ts || 0).getTime() };
        })
        .filter((entry) => entry.name)
        .sort((a, b) => b.timestampMs - a.timestampMs)
        .slice(0, 5)
        .map(({ name, when }) => ({ name, when }));
};

/**
 * Normalizes a trend percentage into an up/down/neutral direction.
 */
const trendDirection = (percent) => {
    if (percent === null || percent === undefined) return null;
    if (percent > 0) return 'up';
    if (percent < 0) return 'down';
    return 'neutral';
};

/**
 * Extracts trend metrics from the heterogeneous trend payloads. Most sensors use
 * a flat shape ({ trendPercent, yesterdayCount }); motion and toilet-flush use a
 * nested raw shape ({ trendResults: { device: { event: { trendPercent } } } }).
 */
const extractTrend = (trendData) => {
    if (!trendData || typeof trendData !== 'object') return { percent: null, count: null };

    if (typeof trendData.trendPercent === 'number' || trendData.yesterdayCount != null) {
        return {
            percent:
                typeof trendData.trendPercent === 'number' ? Math.round(trendData.trendPercent) : null,
            count: trendData.yesterdayCount != null ? Number(trendData.yesterdayCount) : null,
        };
    }

    const trendResults = trendData.trendResults;
    if (trendResults && typeof trendResults === 'object') {
        const percents = [];
        let countSum = 0;
        let hasCount = false;
        Object.values(trendResults).forEach((byDevice) => {
            Object.values(byDevice || {}).forEach((evt) => {
                const percent = get(evt, 'trendPercent', null);
                if (typeof percent === 'number') percents.push(percent);
                const count = get(evt, 'yesterdayCount', null);
                if (count != null) {
                    countSum += Number(count) || 0;
                    hasCount = true;
                }
            });
        });
        return {
            percent: percents.length
                ? Math.round(percents.reduce((a, b) => a + b, 0) / percents.length)
                : null,
            count: hasCount ? countSum : null,
        };
    }

    return { percent: null, count: null };
};

/**
 * Produces a compact, PDF-friendly list of the most recent activity per card.
 * Calls, engagement and biometrics are modeled as detailed sub-lists; smart-home
 * sensors are grouped and include a trend indicator. A sensor is included when
 * it has a latest event OR a meaningful trend (so nothing shown on the dashboard
 * is lost in the report). Order: Calls, Engagement, Motion, Biometrics, Sensors.
 */
export const summarizeRecentActivityForReport = (recentActivityData = {}, ctx = {}) => {
    let callsRow = null;
    let engagementRow = null;
    let motionRow = null;
    const biometricReadings = [];
    const sensorReadings = [];
    const locationBuilder = (latest) =>
        get(latest, 'location', '') || get(latest, EVENT_PROPERTIES.LOCATION, '') || '';

    Object.entries(recentActivityData).forEach(([key, section]) => {
        const data = section?.data;
        const trendData = section?.trendData;

        if (BIOMETRIC_CARD_TYPES.includes(key)) {
            if (isEmpty(data)) return;
            const summary = summarizeBiometric(data, ctx);
            biometricReadings.push({
                label: resolveTitle(ctx.trans, key),
                value: summary.value,
                when: summary.when,
            });
        } else if (key === RECENT_ACTIVITY_CARD_TYPES.RECENT_CALLS) {
            if (isEmpty(data)) return;
            const calls = buildCallRows(data, ctx);
            if (calls.length > 0) {
                callsRow = {
                    type: key,
                    iconKey: 'calls',
                    activity: resolveTitle(ctx.trans, key),
                    calls,
                };
            }
        } else if (key === RECENT_ACTIVITY_CARD_TYPES.ENGAGEMENT) {
            if (isEmpty(data)) return;
            const engagement = buildEngagementRows(data, ctx);
            if (engagement.length > 0) {
                engagementRow = {
                    type: key,
                    iconKey: 'engagement',
                    activity: resolveTitle(ctx.trans, key),
                    engagement,
                };
            }
        } else if (key === RECENT_ACTIVITY_CARD_TYPES.MOTION) {
            const trend = extractTrend(trendData);
            if (isEmpty(data) && trend.count === null && trend.percent === null) return;
            const count = Array.isArray(data) ? data.length : 0;
            const summary = summarizeEventList(data, ctx, locationBuilder);
            const location = summary.value && summary.value !== '-' ? summary.value : '';
            motionRow = {
                type: key,
                iconKey: 'motion',
                activity: resolveTitle(ctx.trans, key),
                value: location || `${count} ${ctx.trans('MOVEMENT_DETECTED') || 'events'}`,
                when: summary.when,
                trendPercent: trend.percent,
                trendDir: trendDirection(trend.percent),
            };
        } else {
            const trend = extractTrend(trendData);
            if (isEmpty(data) && trend.count === null && trend.percent === null) return;
            const summary = summarizeEventList(data, ctx, locationBuilder);
            const location = summary.value && summary.value !== '-' ? summary.value : '';
            const value =
                location ||
                (trend.count !== null ? `${trend.count} ${ctx.trans('TIMES') || 'times'}` : '-');
            sensorReadings.push({
                label: resolveTitle(ctx.trans, key),
                value,
                when: summary.when,
                trendPercent: trend.percent,
                trendDir: trendDirection(trend.percent),
            });
        }
    });

    const rows = [];
    if (callsRow) rows.push(callsRow);
    if (engagementRow) rows.push(engagementRow);
    if (motionRow) rows.push(motionRow);
    if (biometricReadings.length > 0) {
        rows.push({
            type: 'biometrics',
            iconKey: 'biometric',
            activity: ctx.trans('BIOMETRICS') || 'Biometrics',
            readings: biometricReadings.slice(0, 5),
        });
    }
    if (sensorReadings.length > 0) {
        rows.push({
            type: 'smart_home_sensors',
            iconKey: 'smart_home',
            activity: ctx.trans('SMART_HOME_SENSORS') || 'Smart Home Sensors',
            readings: sensorReadings,
        });
    }
    return rows;
};
